import * as React from "react";
import { useContext, useState, useRef, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Popper from "@mui/material/Popper";
import { Box, TextField, Menu } from "@mui/material";
import MenuPopup from "./MenuPopup";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AssessmentIcon from "@mui/icons-material/Assessment";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import SnackbarCustom from "../../components/SnackBarCustom";
import PopoverCustom from "../../components/PopoverCustom";
import { TokenContext } from "../../TokenContext";
import { formatDate, formatDateNotSymbol } from "../../utils/deteUtils";
import LoadingSpinner from "../../components/LoadingSpiner";
import { Save as SaveIcon } from "@mui/icons-material";
import { DisasterExcelRole, DisasterRole } from "../../role";
import { useNavigate } from "react-router-dom";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  getDashboard,
  createAsyncTask,
  getAsyncTasks,
  updateDisaster,
  getDisasterExcel,
} from "../../api";
import type { SnackbarData } from "../../components/SnackBarCustom";
import type { AsyncTaskStat } from "../../types/async-task";
import type {
  DashboardDisasterItem,
  DashboardListOkResponse,
} from "../../types/dashboard";
import type { DisasterKind } from "../../types/disaster";

type DisasterListColumn = {
  id: string;
  label: string;
  minWidth: number;
  align?: "left" | "right" | "center" | "inherit" | "justify";
  isSmallScreen: boolean;
  kind?: boolean;
  textField?: boolean;
  operation?: boolean;
  report?: boolean;
  disaster_excel_com?: boolean;
  disaster_excel_meti?: boolean;
  buttons?: boolean;
  format?: (value: number) => string;
};

type DisasterTextfieldProps = {
  disasterData: DashboardDisasterItem;
  changedValue: (value: string) => void;
  readOnly: boolean;
};

const columns: DisasterListColumn[] = [
  {
    id: "start_at",
    label: "災害対応日時（始）",
    minWidth: 80,
    isSmallScreen: true,
  },
  {
    id: "kind",
    label: "災害種別",
    minWidth: 80,
    isSmallScreen: true,
    kind: true,
  },
  {
    id: "name",
    label: "災害名",
    minWidth: 112,
    align: "left",
    isSmallScreen: true,
    textField: true,
  },
  {
    id: "disaster_status_count",
    label: "被災SS数",
    minWidth: 180,
    align: "left",
    format: (value) => {
      return value.toLocaleString("en-US");
    },
    operation: true,
    isSmallScreen: false,
  },
  {
    id: "report",
    label: "レポート",
    minWidth: 100,
    align: "left",
    report: true,
    isSmallScreen: false,
  },
  {
    id: "disaster_excel_com",
    label: "社内用",
    minWidth: 40,
    align: "left",
    disaster_excel_com: true,
    isSmallScreen: false,
  },
  {
    id: "disaster_excel_meti",
    label: "METI用",
    minWidth: 40,
    align: "left",
    disaster_excel_meti: true,
    isSmallScreen: false,
  },
  {
    id: "excel_at",
    label: "Excel最終作成日",
    minWidth: 80,
    align: "left",
    format: (value) => value.toFixed(2),
    isSmallScreen: false,
  },
  {
    id: "buttonSpace",
    label: "",
    minWidth: 152,
    align: "left",
    format: (value) => value.toFixed(2),
    buttons: true,
    isSmallScreen: false,
  },
  {
    id: "create_user",
    label: "登録者",
    minWidth: 172,
    align: "left",
    format: (value) => value.toFixed(2),
    isSmallScreen: false,
  },
];

const hasExcelUpdate = (
  updateAt: string | null | undefined,
  excelAt: string | null | undefined
): boolean => {
  if (!updateAt) {
    return false;
  }
  if (!excelAt) {
    return true;
  }
  return new Date(updateAt) > new Date(excelAt);
};

const ExcelUpdateBadge = () => (
  <Box
    sx={{
      borderRadius: "20px",
      padding: "4px 8px",
      color: "white",
      fontWeight: "bold",
      width: "fit-content",
      backgroundColor: "orangered",
      fontSize: "12px",
      whiteSpace: "nowrap",
    }}
  >
    更新有り
  </Box>
);

const DisasterTextfield = ({
  disasterData,
  changedValue,
  readOnly,
}: DisasterTextfieldProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (typeof changedValue === "function") {
      changedValue(e.target.value);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: "8px" }}>
      <TextField
        size="small"
        variant="outlined"
        value={disasterData?.name}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleChange(e)}
        sx={{ pointerEvents: "auto" }}
        InputProps={{
          readOnly: readOnly, // ここでreadOnlyを設定
        }}
      />
    </Box>
  );
};

export default function DisasterList() {
  const tokenContext = useContext(TokenContext);
  const user = tokenContext?.user;
  // const dashboardRole = DashboardRole(user.m_organization.m_role_id);
  const disasterExcelRole = DisasterExcelRole(user?.m_organization.m_role_id ?? 0);
  const disasterRole = DisasterRole(user?.m_organization.m_role_id ?? 0);
  //一覧取得
  const [selectedRow, setSelectedRow] = useState<DashboardDisasterItem | null>(
    null
  );
  const [excelStatus, setExcelStatus] = useState<Record<number, AsyncTaskStat>>(
    {}
  );
  const previousExcelStatusRef = useRef<Record<number, AsyncTaskStat>>({});
  // 一括更新のポーリング状態管理
  const [isBulkUpdatePolling, setIsBulkUpdatePolling] = useState(false);
  const [bulkUpdateStatus, setBulkUpdateStatus] = useState<AsyncTaskStat | null>(
    null
  );
  //*ポップアップ設定 */
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleLocationCodeClick = (
    row: DashboardDisasterItem,
    rowNum: number
  ) => {
    setSelectedRow(row);
    setSelectedRowNum(rowNum);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };
  //検索結果ページネーション管理
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //一覧取得
  const [disasters, setDisasters] = useState<DashboardDisasterItem[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>();
  const getDisasterData = async () => {
    try {
      const response = (await getDashboard({
        order: "create_at",
        sort: "desc",
        page: page + 1,
        limit: rowsPerPage,
      })) as DashboardListOkResponse;
      if (response.status !== "ok") {
        return;
      }
      const data = response.disasters;
      const totalCount = response.total_count;
      // テナント情報をステートにセット
      setDisasters(data);
      setTotalCount(totalCount);
    } catch (error) {
      console.error("API呼び出しエラー1", error);
    }
  };

  useEffect(() => {
    getDisasterData();
    if (disasterExcelRole.read) {
      const intervalId = setInterval(() => {
        getCreateExcelTask();
      }, 10000); // 10000ミリ秒 = 10秒
      // クリーンアップ関数
      return () => clearInterval(intervalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  // 更新処理
  const updateDisasterExcel = async (disasterId: number) => {
    try {
      const status = await createAsyncTask({
        kind: "createDisasterExcel",
        opt_id: disasterId,
      });
      console.log("災害更新レスポンス:", status);
      //スナックバーステート
      setSnackBarstatus(status);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  const getCreateExcelTask = async () => {
    try {
      const response = await getAsyncTasks({
        kind: "createDisasterExcel",
        stat: 0,
        order: null,
        sort: null,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (response.status !== "ok") {
        setExcelStatus({});
        setIsBulkUpdatePolling(false);
        setBulkUpdateStatus(1);
        return;
      }
      const data = response.async_tasks;
        // 前回のexcelStatusと比較して、完了した災害IDを特定
        const previousStatus = excelStatus;
        const completedDisasterIds: number[] = [];

        const newExcelStatus: Record<number, AsyncTaskStat> = {};
        for (let i = 0; i < data.length; i++) {
          const task = data[i];
          newExcelStatus[task.opt_id] = task.stat;
          if (
            previousStatus[task.opt_id] === 0 &&
            task.stat !== 0 &&
            task.opt_id !== 0
          ) {
            completedDisasterIds.push(task.opt_id);
          }
        }
        setExcelStatus(newExcelStatus);

        // Excel生成が完了した災害のデータを再取得
        if (completedDisasterIds.length > 0) {
          getDisasterData();
        }

        // 一括更新のポーリング状態をチェック
        if (isBulkUpdatePolling) {
          const bulkTask = data.find((task) => task.opt_id === 0);
          if (bulkTask) {
            setBulkUpdateStatus(bulkTask.stat);
            // statが0以外（完了）になったらポーリングを停止
            if (bulkTask.stat !== 0) {
              setIsBulkUpdatePolling(false);
              // 一括更新が完了したら災害データを再取得
              getDisasterData();
            }
          } else {
            // opt_id=0のタスクが見つからない場合も完了とみなす
            setBulkUpdateStatus(1);
            setIsBulkUpdatePolling(false);
            getDisasterData();
          }
        }
    } catch (error) {
      console.error("API呼び出しエラー", error);
      // エラー時も一括更新ポーリングを停止
      if (isBulkUpdatePolling) {
        setExcelStatus({});
        setIsBulkUpdatePolling(false);
        setBulkUpdateStatus(1);
      }
    }
  };

  const updateButtonClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    disasterId: number
  ) => {
    e.stopPropagation();
    updateDisasterExcel(disasterId);

    // 一括更新の場合（disasterId=0）はポーリング状態を開始
    if (disasterId === 0) {
      setIsBulkUpdatePolling(true);
      setBulkUpdateStatus(0); // ローディング状態
    }
  };

  //完了処理
  const [putDisasterId, setPutDisasterId] = useState<number | undefined>();
  const putComplete = async (isSuccess: boolean) => {
    if (!isSuccess) {
      console.log("何も行いません");
      return;
    }

    try {
      if (putDisasterId === undefined) {
        return;
      }
      const status = await updateDisaster(putDisasterId, {
        id: putDisasterId,
        is_complete: true,
      });
      //スナックバーステート
      setSnackBarstatus(status);
      setSnackbarOpen(true);
      handlePopoverClose();
      getDisasterData();
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  const ButtonVaridate = ({ disasterId }: { disasterId: number }) => {
    return (
      <>
        <Box>
          {disasterExcelRole.update ? (
            <Button
              variant="outlined"
              onClick={(e) => updateButtonClick(e, disasterId)}
              sx={{ pointerEvents: "auto" }}
              aria-label="災害更新"
            >
              更新
            </Button>
          ) : (
            <></>
          )}
          {disasterRole.update ? (
            <Button
              variant="contained"
              onClick={(e) => completeButtonClick(e, disasterId)}
              sx={{ marginLeft: "8px", pointerEvents: "auto" }}
              aria-label="災害完了"
            >
              完了
            </Button>
          ) : (
            <></>
          )}
        </Box>
      </>
    );
  };

  //Excelダウンロード処理
  const getExcelFile = async (
    e: React.MouseEvent<HTMLElement>,
    disasterId: number,
    isMeti: boolean,
    excelAt: string | null | undefined,
    disasterName: string
  ) => {
    e.stopPropagation();
    try {
      const blob = await getDisasterExcel(disasterId, { is_meti: isMeti });

      // バイナリデータからBlob URLを生成
      if (blob.type === "application/json") {
        setSnackBarstatus({
          message: "ダウンロードに失敗しました",
          status: "ng",
        });
        setSnackbarOpen(true);
      } else {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        if (isMeti) {
          a.download = `${disasterName}_METI報告_${formatDateNotSymbol(
            excelAt
          )}.xlsx`; // ダウンロードするファイル名
        } else {
          a.download = `${disasterName}_被害報告社内用_${formatDateNotSymbol(
            excelAt
          )}.xlsx`; // ダウンロードするファイル名
        }
        document.body.appendChild(a); // 一時的にリンクをDOMに追加
        a.click(); // リンクをクリックしてダウンロードを開始
        a.remove(); // リンクを削除
        window.URL.revokeObjectURL(url); // ObjectURLを解放
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  // 被災SS数カラム
  const OperationValidate = ({
    disaster,
  }: {
    disaster: DashboardDisasterItem;
  }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    // 被災SS数と報告済SS数を取得
    const disasterStatusCount = disaster["disaster_status_count"] || 0;
    const disasterStatusEditCount = disaster["disaster_status_edit_count"] || 0;
    // 報告率を計算（被災SS数が0の場合は0%）
    const reportRate = disasterStatusCount > 0
      ? ((disasterStatusEditCount / disasterStatusCount) * 100).toFixed(1)
      : 0;

    // console.log("disaster:", disaster);
    return (
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: "8px", width: "100%" }}>
        <Box

          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: "32px",
            cursor: "pointer",
            // pointerEvents: "auto",
            // "&:hover": {
            //   opacity: 0.8,
            //   textDecoration: "underline",
            // },
          }}
        >
          <Box>{disasterStatusCount}</Box>

        </Box>
        <Box
          sx={{
            minWidth: "200px",
            display: "flex",
            gap: "8px",
            alignItems: "center",

          }}
        >
          <IconButton
            onClick={handleMenuClick}
            color="primary"
            size="small"
            sx={{ pointerEvents: "auto" }}
          >
            <LaunchIcon sx={{ width: "20px" }} />
          </IconButton>
          <Box sx={{
            display: "block", "@media screen and (max-width:900px)": {
              display: "none",
            },
          }}>
            <MenuPopup disasterId={disaster["id"]} />
          </Box>


        </Box>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Box sx={{ maxHeight: "500px", overflowY: "auto" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                paddingX: "8px",
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 1,
              }}
            >
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Table size="small">
              <TableHead
                sx={{ position: "sticky", top: 40, backgroundColor: "#fff", zIndex: 1 }}
              >
                <TableRow>
                  <TableCell>被災SS数</TableCell>
                  <TableCell>報告済SS数</TableCell>
                  <TableCell>報告率</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{disasterStatusCount}</TableCell>
                  <TableCell>{disasterStatusEditCount}</TableCell>
                  <TableCell>{reportRate}%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Box sx={{ height: "16px", position: "sticky", bottom: 0 }} />
          </Box>
        </Menu>
      </Box>
    );
  };
  // Excel社内用ダウンロードボタン
  const ExcelComValidate = ({
    disasterId,
    excelExists,
    disasterName,
  }: {
    disasterId: number;
    excelExists?: string | null;
    disasterName: string;
  }) => {
    // console.log(
    //   "disasterId:",
    //   disasterId,
    //   "excelExists:",
    //   excelExists,
    //   "disasterName:",
    //   disasterName
    // );
    return (
      <>
        <Box sx={{ display: "flex", gap: "2px", alignItems: "center" }}>
          {excelStatus[disasterId] === 0 ? (
            <LoadingSpinner />
          ) : excelExists ? (
            <IconButton
              sx={{ pointerEvents: "auto" }}
              onClick={(e) =>
                getExcelFile(e, disasterId, false, excelExists, disasterName)
              }
            >
              <DownloadIcon color="primary" />
            </IconButton>
          ) : (
            ""
          )}
        </Box>
      </>
    );
  };
  // ExcelMETI用ダウンロードボタン
  const ExcelMetiValidate = ({
    disasterId,
    excelExists,
    disasterName,
  }: {
    disasterId: number;
    excelExists?: string | null;
    disasterName: string;
  }) => {
    return (
      <>
        <Box sx={{ display: "flex", gap: "2px", alignItems: "center" }}>
          {excelStatus[disasterId] === 0 ? (
            <LoadingSpinner />
          ) : excelExists ? (
            <IconButton
              sx={{ pointerEvents: "auto" }}
              onClick={(e) =>
                getExcelFile(e, disasterId, true, excelExists, disasterName)
              }
            >
              <DownloadIcon color="secondary" />
            </IconButton>
          ) : (
            ""
          )}
        </Box>
      </>
    );
  };

  // 一括用ダウンロードボタン
  const ExcelAllValidate = () => {
    const [localPopperOpen, setLocalPopperOpen] = React.useState(false);
    const [localAnchorEl, setLocalAnchorEl] =
      React.useState<null | HTMLElement>(null);

    // 一括更新のローディング状態をチェック
    const isBulkLoading = isBulkUpdatePolling && bulkUpdateStatus === 0;

    const handleLocalPopperOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      // console.log("handleLocalPopperOpen called", e.currentTarget);
      setLocalAnchorEl(e.currentTarget);
      setLocalPopperOpen((prevOpen) => !prevOpen);
    };

    const handleLocalPopperMouseEnter = (
      e: React.MouseEvent<HTMLButtonElement>
    ) => {
      e.stopPropagation();
      // console.log("handleLocalPopperMouseEnter called", e.currentTarget);
      setLocalAnchorEl(e.currentTarget);
      setLocalPopperOpen(true);
    };

    const handleLocalPopperMouseLeave = (
      e: React.MouseEvent<HTMLButtonElement>
    ) => {
      e.stopPropagation();
      // 少し遅延を入れて、メニューにマウスが移動する時間を確保
      setTimeout(() => {
        // メニュー内にマウスがある場合は閉じない
        const relatedTarget = e.relatedTarget as HTMLElement | null;
        if (
          !relatedTarget ||
          (!relatedTarget.closest('[role="menu"]') &&
            !relatedTarget.closest(
              '[aria-label="Button group with a nested menu"]'
            ))
        ) {
          setLocalPopperOpen(false);
          setLocalAnchorEl(null);
        }
      }, 150);
    };

    return (
      <>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                sx={{
                  whiteSpace: "nowrap",
                  pointerEvents: "auto",
                  borderTopRightRadius: "0px",
                  borderBottomRightRadius: "0px",
                  boxShadow: 0,
                }}
                variant="outlined"
                onClick={(e) => updateButtonClick(e, 0)} //disasterId=0
              >
                一括更新
              </Button>
              <Button
                sx={{
                  pointerEvents: isBulkLoading ? "none" : "auto",
                  borderTopLeftRadius: "0px",
                  borderBottomLeftRadius: "0px",
                  boxShadow: 0,
                  opacity: isBulkLoading ? 0.6 : 1,
                }}
                aria-controls={
                  localPopperOpen ? "split-button-menu" : undefined
                }
                aria-expanded={localPopperOpen ? "true" : undefined}
                aria-label="select merge strategy"
                aria-haspopup="menu"
                onClick={isBulkLoading ? undefined : handleLocalPopperOpen}
                onMouseEnter={
                  isBulkLoading ? undefined : handleLocalPopperMouseEnter
                }
                onMouseLeave={
                  isBulkLoading ? undefined : handleLocalPopperMouseLeave
                }
                variant="contained"
                disabled={isBulkLoading}
              >
                <Box
                  sx={{
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {isBulkLoading && <LoadingSpinner />}
                  一括DL
                  {!isBulkLoading && <ArrowDropDownIcon />}
                </Box>
              </Button>
              <Popper
                sx={{ zIndex: 9999 }}
                open={localPopperOpen && Boolean(localAnchorEl)}
                anchorEl={localAnchorEl}
                placement="bottom-end"
                role={undefined}
                transition
                disablePortal={false}
                modifiers={[
                  {
                    name: "preventOverflow",
                    enabled: true,
                    options: {
                      altAxis: true,
                      tether: false,
                    },
                  },
                  {
                    name: "flip",
                    enabled: true,
                  },
                ]}
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === "bottom" ? "center top" : "center bottom",
                    }}
                  >
                    <Paper
                      sx={{
                        pointerEvents: "auto",
                        position: "relative",
                        zIndex: 9999,
                        boxShadow:
                          "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)",
                      }}
                      onMouseEnter={() => {
                        setLocalPopperOpen(true);
                      }}
                      onMouseLeave={(e) => {
                        // メニュー外に移動した場合のみ閉じる
                        const relatedTarget = e.relatedTarget as HTMLElement | null;
                        if (
                          !relatedTarget ||
                          (!relatedTarget.closest('[role="menu"]') &&
                            !relatedTarget.closest(
                              '[aria-label="Button group with a nested menu"]'
                            ))
                        ) {
                          setTimeout(() => {
                            setLocalPopperOpen(false);
                            setLocalAnchorEl(null);
                          }, 150);
                        }
                      }}
                    >
                      <MenuList id="split-button-menu" autoFocusItem>
                        <MenuItem
                          key={"ExcelCom"}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                          onClick={(e) => {
                            getExcelFile(
                              e,
                              0, //disasterId=0
                              false,
                              new Date().toISOString(),
                              "全災害"
                            );
                          }}
                        >
                          <>社内用</>
                          <DownloadIcon color="primary" />
                        </MenuItem>
                        <MenuItem
                          key={"ExcelMeti"}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                          onClick={(e) => {
                            getExcelFile(
                              e,
                              0, //disasterId=0
                              true,
                              new Date().toISOString(),
                              "全災害"
                            );
                          }}
                        >
                          <>METI用</>
                          <DownloadIcon color="secondary" />
                        </MenuItem>
                      </MenuList>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Box>
        </Box>
      </>
    );
  };
  const disasterValidate = (disasterName?: DisasterKind) => {
    return (
      <Box
        sx={{
          borderRadius: "20px",
          padding: "8px",
          color: "white",
          fontWeight: "bold",
          width: "fit-content",
          backgroundColor:
            disasterName === "quake"
              ? "red"
              : disasterName === "w-r-s"
                ? "orange"
                : disasterName === "tsunami"
                  ? "mediumblue"
                  : disasterName === "manual"
                    ? "gray"
                    : "black",
        }}
      >
        {disasterName === "quake"
          ? "地震"
          : disasterName === "w-r-s"
            ? "雨風雪"
            : disasterName === "tsunami"
              ? "津波"
              : disasterName === "manual"
                ? "手動"
                : disasterName === "alert"
                  ? "警報"
                  : "その他"}
      </Box>
    );
  };

  //スナックバー管理
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  //完了確認ポップオーバー管理
  const [confirmPopoverOpen, setConfirmPopoverOpen] = useState(false);
  const completeButtonClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    disasterId: number
  ) => {
    e.stopPropagation();
    setPutDisasterId(disasterId);
    setConfirmPopoverOpen(true);
  };
  const confirmPopupClose = () => {
    setConfirmPopoverOpen(false);
  };

  //登録ボタン選択行番号管理
  const [selectedRowNum, setSelectedRowNum] = useState<number | "">("");

  const handleChangeDisaster = (inputValue: string, siteId: number) => {
    const updatedArray = [...disasters]; // オリジナルの配列をコピー
    //sites配列内からidが当てはまる配列を探す
    const updatedItem = updatedArray.find((item) => item.id === siteId);
    if (updatedItem) {
      updatedItem.name = inputValue;
      setDisasters(updatedArray);
    } else {
      console.error(`sites with id ${siteId} not found`);
    }
  };

  //災害名登録ポップオーバー管理
  const [putNamePopoverOpen, setPutNamePopoverOpen] = useState(false);
  const putNameButtonClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    rowNum: number
  ) => {
    e.stopPropagation();
    setSelectedRowNum(rowNum);
    setPutNamePopoverOpen(true);
  };
  const putNamePopupClose = () => {
    setPutNamePopoverOpen(false);
  };

  //災害名更新
  const putDisasterName = async (isSuccess: boolean) => {
    if (!isSuccess) {
      console.log("何も行いません");
      return;
    }

    if (selectedRowNum === "" || !disasters[selectedRowNum]) {
      return;
    }

    try {
      const status = await updateDisaster(disasters[selectedRowNum].id, {
        id: disasters[selectedRowNum].id,
        name: disasters[selectedRowNum].name,
        is_complete: false,
      });
      //スナックバーステート
      setSnackBarstatus(status);
      setSnackbarOpen(true);
      getDisasterData();
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  //災害名更新ボタン
  const PutButton = ({ rowNum }: { rowNum: number }) => {
    return (
      <IconButton
        color="primary"
        sx={{ pointerEvents: "auto" }}
        onClick={(e) => putNameButtonClick(e, rowNum)}
        aria-label="災害名保存"
      >
        <SaveIcon />
      </IconButton>
    );
  };

  //レポートボタン
  const ReportButton = ({ disasterId }: { disasterId: number }) => {
    const navigate = useNavigate();

    const handleReportClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      navigate(`/dashboard/report/internal-use?disaster-id=${disasterId}`);
    };

    return (
      <IconButton
        name="reportButton"
        color="primary"
        sx={{ pointerEvents: "auto" }}
        onClick={handleReportClick}
        aria-label="レポート表示"
      >
        <AssessmentIcon />
      </IconButton>
    );
  };
  return (
    <Paper sx={{ width: "100%", overflow: "visible", boxShadow: "none" }}>
      {/* <Box>Dashboard2
        <Button onClick={consoleCheck}>コンソール確認</Button>
      </Box> */}
      <TableContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "8px",
            "@media screen and (max-width:900px)": {
              display: "none",
            },
          }}
        >
          <ExcelAllValidate />
        </Box>
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={{ overflow: "visible" }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    color: "outline.dark",
                    fontWeight: "bold",
                    borderBottom: 2,
                    padding: "8px",
                    "@media screen and (max-width:900px)": {
                      display:
                        column.isSmallScreen === true ? "table-cell" : "none",
                    },
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {disasters?.map((disaster, rowNum) => {
              return (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={disaster.id}
                  onClick={() => handleLocationCodeClick(disaster, rowNum)}
                  sx={{
                    pointerEvents: "none",
                    "@media screen and (max-width:900px)": {
                      cursor: "pointer",
                      pointerEvents: "auto",
                    },
                  }}
                >
                  {columns.map((column, index) => {
                    return (
                      <TableCell
                        key={disaster.id + column.id}
                        align={column.align}
                        sx={{
                          padding: "8px",
                          overflow: "visible",
                          "@media screen and (max-width:900px)": {
                            display:
                              column.isSmallScreen === true
                                ? "table-cell"
                                : "none",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "24px",
                          }}
                        >
                          {column.kind ? (
                            disasterValidate(disaster.kind)
                          ) : column.buttons ? (
                            <ButtonVaridate disasterId={disaster["id"]} />
                          ) : column.operation ? (
                            <OperationValidate
                              disaster={disaster}
                            />
                          ) : column.disaster_excel_com ? (
                            disasterExcelRole.read ? (
                              <ExcelComValidate
                                disasterId={disaster.id}
                                excelExists={disaster.excel_at}
                                disasterName={disaster.name}
                              />
                            ) : (
                              <></>
                            )
                          ) : column.disaster_excel_meti ? (
                            disasterExcelRole.read ? (
                              <ExcelMetiValidate
                                disasterId={disaster.id}
                                excelExists={disaster.excel_at}
                                disasterName={disaster.name}
                              />
                            ) : (
                              <></>
                            )
                          ) : column.textField ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <DisasterTextfield
                                disasterData={disaster}
                                changedValue={(value) =>
                                  handleChangeDisaster(value, disaster.id)
                                }
                                readOnly={!disasterRole.update}
                              />
                              {disasterRole.update ? (
                                <PutButton rowNum={rowNum} />
                              ) : (
                                <></>
                              )}
                            </Box>
                          ) : column.id === "start_at" ? (
                            formatDate(disaster[column.id])
                          ) : column.id === "excel_at" ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                flexWrap: "wrap",
                              }}
                            >
                              {formatDate(disaster.excel_at)}
                              {hasExcelUpdate(
                                disaster.disaster_status_max_update_at,
                                disaster.excel_at
                              ) ? (
                                <ExcelUpdateBadge />
                              ) : null}
                            </Box>
                          ) : column.id === "create_user" ? (
                            <>{disaster["create_user"]?.["name"]}</>
                          ) : column.id === "report" ? (
                            <ReportButton disasterId={disaster["id"]} />
                          ) : (
                            <>{disaster[column.id]}</>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
        <Box
          sx={{
            display: "flex",
            height: "56px",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px",
            borderBottom: "2px solid #CCC",
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              paddingLeft: "8px",
              fontWeight: "bold",
              fontSize: "20px",
            }}
          >
            対応中災害詳細
          </Box>
          <IconButton
            aria-label="close"
            onClick={handlePopoverClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
          <TableBody>
            {selectedRow &&
              columns
                .slice(0, 5)
                .concat(columns.slice(9, 10))
                .map((column) => {
                  return (
                    <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                      <TableCell
                        align={column.align}
                        sx={{
                          color: "outline.dark",
                          fontWeight: "bold",
                          boxSizing: "border-box",
                          width: "calc((100vw - 64px) / 2)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            WebkitWritingMode: "horizontal-tb",
                            height: "100%",
                          }}
                        >
                          {column.label}
                        </Box>
                      </TableCell>
                      <TableCell
                        align={column.align}
                        sx={{
                          writingMode: "horizontal-tb",
                          boxSizing: "border-box",
                          width: "calc((100% - 64px) / 2)",
                          overflowWrap: "break-word",
                        }}
                      >
                        {column.kind ? (
                          disasterValidate(selectedRow["kind"]) // column.kindが存在する場合
                        ) : column.operation ? (
                          // <Box>
                          //   <>{selectedRow[column.id]}</>
                          // </Box>
                          <OperationValidate
                            disaster={selectedRow}
                          />
                        ) : column.textField ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <DisasterTextfield
                              disasterData={selectedRow}
                              changedValue={(value) =>
                                handleChangeDisaster(value, selectedRow.id)
                              }
                              readOnly={!disasterRole.update}
                            />
                            {disasterRole.update && selectedRowNum !== "" ? (
                              <PutButton rowNum={selectedRowNum} />
                            ) : (
                              <></>
                            )}
                          </Box>
                        ) : column.id === "start_at" ? (
                          formatDate(selectedRow[column.id])
                        ) : column.id === "excel_at" ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              flexWrap: "wrap",
                            }}
                          >
                            {formatDate(selectedRow.excel_at)}
                            {hasExcelUpdate(
                              selectedRow.disaster_status_max_update_at,
                              selectedRow.excel_at
                            ) ? (
                              <ExcelUpdateBadge />
                            ) : null}
                          </Box>
                        ) : column.id === "create_user" ? (
                          <>{selectedRow["create_user"]?.["name"]}</>
                        ) : column.id === "report" ? (
                          <ReportButton disasterId={selectedRow.id} />
                        ) : (
                          <>{selectedRow[column.id]}</>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
        <Box
          sx={{
            margin: "16px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "16px",
          }}
        >
          <MenuPopup disasterId={selectedRow?.id ?? 0} />
          <ButtonVaridate disasterId={selectedRow?.id ?? 0} />
        </Box>
      </Dialog>

      <TablePagination
        rowsPerPageOptions={[10, 30, 50, 100]}
        component="div"
        count={totalCount ?? 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
          {
            margin: "0 !important",
            padding: "0 !important",
          },
        }}
        labelRowsPerPage="表示件数:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`}
      />
      <SnackbarCustom
        data={snackBarstatus}
        openStatus={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      />
      <PopoverCustom
        open={confirmPopoverOpen}
        onClose={confirmPopupClose}
        yesNo={(value) => putComplete(value)}
        title={"完了確認"}
        contents={"完了してもよろしいですか？"}
      />
      <PopoverCustom
        open={putNamePopoverOpen}
        onClose={putNamePopupClose}
        yesNo={(value) => putDisasterName(value)}
        title={"災害名登録確認"}
        contents={"災害名を登録してもよろしいですか？"}
      />
    </Paper>
  );
}
